import React from "react";
import styled from "styled-components";
import { Check } from "react-feather";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import type {
  ConnectorStepConfig,
  ConnectorStepStatus,
} from "./ConnectorWizardModal.types";

interface StepIndicatorProps {
  steps: ConnectorStepConfig[];
  statuses: ConnectorStepStatus[];
  currentIndex: number;
  /** Optional: clicking a completed step jumps back. Pending steps stay disabled. */
  onStepClick?: (index: number) => void;
}

/** Figma uses 60px side-padding so the connector line starts/ends inside the
 *  outermost circles. The base 4px scale doesn't expose 15 (60px), so this
 *  composes 56 + 4 to stay token-driven. `min-width: 0` + `box-sizing` keep
 *  the track inside its parent on narrow viewports. */
const Track = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0 calc(${spacing[14]} + ${spacing[1]});
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

/** Connector line behind the step circles — sits at the vertical center of the 40px circle. */
const ConnectorLine = styled.div`
  position: absolute;
  top: 20px;
  left: 100px;
  right: 100px;
  height: 1px;
  background-color: ${rawColors.stroke[200]};
  z-index: 0;
`;

const StepBlock = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 80px;
  flex: 0 0 auto;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 4px;
    border-radius: 8px;
  }
`;

/** Stable footprint — 2px border on every status (transparent when "pending"
 *  effectively reads as 1px to the eye via colour) so the circle's size
 *  never changes between transitions and the indicator track doesn't
 *  reflow. Active/completed/error are signalled purely through colour. */
const Circle = styled.div<{ $status: ConnectorStepStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background-color: ${rawColors.structural.white};
  border: 2px solid;
  border-color: ${({ $status }) => {
    switch ($status) {
      case "active":
        return rawColors.primary[400];
      case "completed":
        return rawColors.accent.green500;
      case "error":
        return rawColors.accent.red500;
      default:
        return rawColors.stroke[300];
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case "active":
        return rawColors.primary[500];
      case "completed":
        return rawColors.accent.green500;
      case "error":
        return rawColors.accent.red500;
      default:
        return rawColors.text.lowEmphasis;
    }
  }};
`;

/** Stable size — same typography variant across all statuses; weight alone
 *  shifts on active so the layout doesn't reflow when a step transitions. */
const StepNumber = styled.span<{ $status: ConnectorStepStatus }>`
  ${typographyMixin("body", 2)}
  font-weight: ${({ $status }) => ($status === "active" ? 600 : 400)};
`;

const StepLabel = styled.span<{ $status: ConnectorStepStatus }>`
  ${typographyMixin("body", 3)}
  font-weight: ${({ $status }) => ($status === "active" ? 600 : 400)};
  color: ${({ $status }) => {
    switch ($status) {
      case "active":
        return rawColors.primary[500];
      case "completed":
      case "error":
        return rawColors.text.highEmphasis;
      default:
        return rawColors.text.mediumEmphasis;
    }
  }};
  white-space: nowrap;
`;

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  statuses,
  currentIndex,
  onStepClick,
}) => (
  <Track role="list" aria-label="Connector setup progress">
    <ConnectorLine aria-hidden="true" />
    {steps.map((step, i) => {
      const status = statuses[i] ?? "pending";
      const clickable =
        Boolean(onStepClick) && (status === "completed" || i < currentIndex);
      return (
        <StepBlock
          key={step.id}
          role="listitem"
          type="button"
          disabled={!clickable}
          aria-current={status === "active" ? "step" : undefined}
          onClick={clickable ? () => onStepClick?.(i) : undefined}
        >
          <Circle $status={status}>
            {status === "completed" ? (
              <Check size={20} strokeWidth={2.5} aria-hidden="true" />
            ) : (
              <StepNumber $status={status}>{i + 1}</StepNumber>
            )}
          </Circle>
          <StepLabel $status={status}>{step.label}</StepLabel>
        </StepBlock>
      );
    })}
  </Track>
);
